/*
 * Navbar — GSF Arbitral AI Vitrine
 * Design: Marine #0D1B2A exact, Or métallique #C9A84C, Cormorant Garamond + Inter
 */
import { useState, useEffect } from "react";
import { Menu, X, Scale } from "lucide-react";
import { LoginDialog } from "@/components/LoginDialog";

const MARINE = "oklch(0.175 0.038 243)";
const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

const navLinks = [
  { label: "Plateforme", href: "#plateforme" },
  { label: "Fonctionnalités", href: "#fonctionnalites" },
  { label: "Conformité OHADA", href: "#ohada" },
  { label: "Sécurité", href: "#securite" },
  { label: "Règlement", href: "#reglement" },
  { label: "Ressources", href: "#ressources" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginRole, setLoginRole] = useState<string | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const openLogin = (role: string) => {
    setMobileOpen(false);
    setLoginRole(role);
    setLoginOpen(true);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled
          ? "color-mix(in oklch, oklch(0.115 0.038 243) 97%, transparent)"
          : "color-mix(in oklch, oklch(0.175 0.038 243) 95%, transparent)",
        backdropFilter: "blur(12px)",
        borderBottom: scrolled ? `1px solid color-mix(in oklch, ${GOLD} 20%, transparent)` : "none",
      }}
    >
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 no-underline"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            <div
              className="w-9 h-9 flex items-center justify-center rounded-sm"
              style={{
                background: "color-mix(in oklch, oklch(0.695 0.105 68) 15%, transparent)",
                border: `1px solid color-mix(in oklch, ${GOLD} 40%, transparent)`,
              }}
            >
              <Scale size={18} style={{ color: GOLD }} />
            </div>
            <div className="flex flex-col leading-tight">
              <span
                className="font-bold text-base tracking-wide"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)", lineHeight: 1.1 }}
              >
                GSF Arbitral AI
              </span>
              <span
                className="text-xs tracking-widest uppercase"
                style={{ color: GOLD, fontSize: "0.6rem", letterSpacing: "0.15em" }}
              >
                Plateforme Fast-Track OHADA
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-sm font-medium transition-colors duration-200"
                style={{
                  color: "oklch(0.85 0.01 243)",
                  background: "none",
                  border: "none",
                  padding: "0",
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: "0.02em",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "oklch(0.85 0.01 243)")}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => openLogin("Avocat")}
              className="gsf-btn-outline"
              style={{ fontSize: "0.75rem", padding: "0.5rem 1.25rem" }}
            >
              Accès Avocat
            </button>
            <button
              type="button"
              onClick={() => openLogin("Arbitre")}
              className="gsf-btn-primary"
              style={{ fontSize: "0.75rem", padding: "0.5rem 1.25rem" }}
            >
              Accès Arbitre
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2"
            style={{ color: GOLD, background: "none", border: "none" }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="lg:hidden"
          style={{
            background: MARINE_DEEP,
            borderTop: `1px solid color-mix(in oklch, ${GOLD} 15%, transparent)`,
          }}
        >
          <div className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-left py-3 px-2 text-sm font-medium transition-colors"
                style={{
                  color: "oklch(0.85 0.01 243)",
                  background: "none",
                  border: "none",
                  borderBottom: `1px solid color-mix(in oklch, ${GOLD} 10%, transparent)`,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {link.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => openLogin("Avocat")}
              className="gsf-btn-outline mt-3 text-center justify-center"
              style={{ fontSize: "0.8rem" }}
            >
              Accès Avocat
            </button>
            <button
              type="button"
              onClick={() => openLogin("Arbitre")}
              className="gsf-btn-primary mt-2 text-center justify-center"
              style={{ fontSize: "0.8rem" }}
            >
              Accès Arbitre
            </button>
          </div>
        </div>
      )}

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} role={loginRole} />
    </header>
  );
}
